/**
 * W-22 个人中心(已登录)。视觉真源 design/me.html。
 * 身份卡(profile + 实名 badge + 编辑资料入口)+ 分组入口(交易 / 卖家 / 其他)。
 * profile 失败静默退化为占位文案(无 error UI / 无重试,语义同 App)。
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMyProfile } from '@/api/profile';
import { getEarningsSummary } from '@/api/wallet';
import { useAuthStore } from '@/store/auth';
import { formatYuan } from '@/lib/formatMoney';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const VERIFIED_STATES = new Set(['KYC_VERIFIED', 'WITHDRAW_BOUND']);

export function MePage() {
  const navigate = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const userId = useAuthStore((s) => s.userId);
  const identityState = useAuthStore((s) => s.identityState);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const profileQuery = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: getMyProfile,
    retry: false, // 失败静默退化,不重试刷错
  });

  // 收益概览:卖家有数据;非卖家 / 失败 → 不显数字(优雅降级,不阻塞页面)。
  const earningsQuery = useQuery({
    queryKey: ['me', 'earnings-overview'],
    queryFn: getEarningsSummary,
    retry: false,
  });

  const profile = profileQuery.data;
  const nickname = profile?.nickname?.trim() || '未设置昵称';
  const bio = profile?.bio?.trim() || '这个人很懒,还没有简介';
  const avatarChar = profile?.nickname?.trim()?.[0] ?? '我';
  const verified = !!identityState && VERIFIED_STATES.has(identityState);
  const cumulative = earningsQuery.data?.cumulativeAmount;

  const onLogout = () => {
    clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className="inner section">
      {/* 身份卡 */}
      <section className="card card-pad mb-8">
        <div className="row between wrap gap-6">
          <div className="row gap-6">
            {profile?.avatar ? (
              <span
                className="avatar avatar-lg"
                style={{
                  backgroundImage: `url(${profile.avatar})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                aria-label="头像"
              />
            ) : (
              <span className="avatar avatar-lg">{avatarChar}</span>
            )}
            <div className="col gap-2">
              <div className="row gap-2" style={{ alignItems: 'center' }}>
                <span className="h2">{nickname}</span>
                {verified && <span className="badge badge-primary">✓ 已实名</span>}
              </div>
              <div className="sm muted">{bio}</div>
              {userId && <div className="xs muted mt-2">UID {userId}</div>}
            </div>
          </div>
          <Link className="btn btn-secondary" to="/profile/edit">
            编辑资料
          </Link>
        </div>
      </section>

      {/* 入口:交易 / 卖家 */}
      <div
        className="detail-cols"
        style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-8)', alignItems: 'start' }}
      >
        <div>
          <h3 className="h3 mb-4">交易</h3>
          <nav className="list">
            <Link className="list-item" to="/orders">
              <span style={{ flex: 1 }}>我的订单</span>
              <span className="muted">›</span>
            </Link>
            <Link className="list-item" to="/earnings">
              <span style={{ flex: 1 }}>我的钱包 · 收益</span>
              {typeof cumulative === 'number' && (
                <span className="xs muted">累计 {formatYuan(cumulative)}</span>
              )}
              <span className="muted">›</span>
            </Link>
          </nav>
        </div>

        <div>
          <h3 className="h3 mb-4">卖家</h3>
          <nav className="list">
            <Link className="list-item" to="/seller-products">
              <span style={{ flex: 1 }}>我发布的商品</span>
              <span className="muted">›</span>
            </Link>
            <Link className="list-item" to="/withdraw-account">
              <span style={{ flex: 1 }}>收款账户</span>
              <span className="muted">›</span>
            </Link>
          </nav>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="h3 mb-4">其他</h3>
        <nav className="list">
          <Link className="list-item" to="/agreement">
            <span style={{ flex: 1 }}>用户协议</span>
            <span className="muted">›</span>
          </Link>
          <Link className="list-item" to="/privacy-policy">
            <span style={{ flex: 1 }}>隐私政策</span>
            <span className="muted">›</span>
          </Link>
          <button
            type="button"
            className="list-item"
            style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
            onClick={() => setConfirmLogout(true)}
          >
            <span style={{ flex: 1, color: 'var(--color-destructive)' }}>退出登录</span>
            <span className="muted">›</span>
          </button>
        </nav>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        title="退出登录"
        description="确认退出当前账号?"
        confirmLabel="退出"
        confirmVariant="destructive"
        onConfirm={onLogout}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
}
